/**
 * Persistent chrome elements — extracted from root view transitions so they
 * stay static while page content animates.
 *
 * Single source of truth. Components import the name they need. CSS rules
 * are injected at startup so ::view-transition pseudo-selectors stay in sync.
 *
 * To add a new persistent chrome element:
 *   1. Add its name to CHROME_NAMES below.
 *   2. Set `style="view-transition-name: {CHROME.XXX}"` on the element.
 *   3. CSS rules are generated automatically from the constant array.
 */

export const CHROME = /** @type {const} */ ({
  HEADER: 'header',
  BOTTOM_BAR: 'bottom-bar',
  SIDEBAR: 'sidebar',
})

export const CHROME_NAMES = Object.values(CHROME)

let injected = false

/** Inject ::view-transition-* rules for all chrome elements. Idempotent. */
export function injectChromeStyles() {
  if (injected || typeof document === 'undefined') return
  injected = true

  const oldNewNames = CHROME_NAMES.flatMap((n) => [
    `::view-transition-old(${n})`,
    `::view-transition-new(${n})`,
  ]).join(',\n')

  // Persistent chrome pattern: pin both snapshots opaque and unanimated, so the
  // element reads as static instead of cross-fading with the page.
  //
  // Two things here are load-bearing, both verified against WebKit and Chromium
  // (the engines disagree, and only this recipe satisfies both):
  //
  //   - Do NOT touch ::view-transition-group. Its position and size come from
  //     the UA-generated keyframe animation; cancelling that leaves the group's
  //     geometry undefined.
  //   - Do NOT hide the old snapshot with `display: none`. Chromium tolerates
  //     it, but in WebKit it takes the whole image-pair down with it and the
  //     element disappears for the length of the transition — this was the
  //     bottom-bar flash on iOS. Leaving both snapshots opaque is enough: the
  //     new one paints over the old.
  //
  // `mix-blend-mode: normal` overrides the UA's `plus-lighter`, which would
  // over-brighten two stacked opaque snapshots.
  const style = document.createElement('style')
  style.textContent = `
${oldNewNames} {
  animation: none !important;
  mix-blend-mode: normal;
  opacity: 1;
}`
  document.head.appendChild(style)
}
